import { type DragEvent, useMemo, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { usePdfPageThumbnails, type PdfPageThumbnail } from '@/features/pdf-page-preview'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  LARGE_PDF_WARNING_PAGES,
  MAX_FILE_MB,
  buildInitialPageOrder,
  countMovedPages,
  isOriginalOrder,
  readPdfPageCount,
  reorderedPdfFilename,
  reorderPdfPages,
  validateFileSize,
  type PageOrderItem,
  type ReorderProgress,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('reorder-pdf-pages').analyticsKey

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading'; file: File }
  | { kind: 'ready'; file: File; totalPages: number; pages: PageOrderItem[] }
  | { kind: 'loading'; file: File; totalPages: number; pages: PageOrderItem[]; progress: ReorderProgress }
  | {
      kind: 'success'
      file: File
      blob: Blob
      objectUrl: string
      totalPages: number
      movedPages: number
    }
  | { kind: 'error'; file: File | null; totalPages: number | null; message: string; errorCode: string }

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file || !file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

interface FileBadgeProps {
  file: File
  totalPages: number | null
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, totalPages, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">
          {formatBytes(file.size)}
          {totalPages !== null && ` · ${format(t.reorderPdfPages.pageCount, { pages: totalPages })}`}
        </p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

interface PageGridProps {
  pages: PageOrderItem[]
  thumbnailByPage: ReadonlyMap<number, PdfPageThumbnail>
  disabled: boolean
  onMove: (fromIndex: number, toIndex: number) => void
  onReset: () => void
}

function PageGrid({ pages, thumbnailByPage, disabled, onMove, onReset }: PageGridProps) {
  const t = useT()
  const [draggedId, setDraggedId] = useState<string | null>(null)

  function handleDragStart(e: DragEvent<HTMLDivElement>, page: PageOrderItem) {
    if (disabled) return
    setDraggedId(page.id)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', page.id)
  }

  function handleDrop(e: DragEvent<HTMLDivElement>, targetIndex: number) {
    e.preventDefault()
    const pageId = draggedId ?? e.dataTransfer.getData('text/plain')
    const fromIndex = pages.findIndex((page) => page.id === pageId)
    if (fromIndex !== -1 && fromIndex !== targetIndex) {
      onMove(fromIndex, targetIndex)
    }
    setDraggedId(null)
  }

  const movedPages = countMovedPages(pages)

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">{t.reorderPdfPages.pagesTitle}</h2>
          <p className="text-xs text-muted-foreground">
            {movedPages > 0
              ? format(t.reorderPdfPages.changedSummary, { count: movedPages })
              : t.reorderPdfPages.noChanges}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onReset}
          disabled={disabled || isOriginalOrder(pages)}
        >
          {t.reorderPdfPages.resetOrder}
        </Button>
      </div>

      <div className="max-h-[560px] overflow-y-auto rounded-lg border bg-background p-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {pages.map((page, index) => {
            const originalPage = page.originalIndex + 1
            const currentPage = index + 1
            const isDragged = draggedId === page.id
            const thumbnail = thumbnailByPage.get(originalPage)

            return (
              <div
                key={page.id}
                draggable={!disabled}
                onDragStart={(e) => handleDragStart(e, page)}
                onDragEnd={() => setDraggedId(null)}
                onDragOver={(e) => {
                  if (!disabled) e.preventDefault()
                }}
                onDrop={(e) => !disabled && handleDrop(e, index)}
                className={cn(
                  'group min-h-[150px] rounded-md border bg-card p-3 shadow-sm transition-colors',
                  'focus-within:ring-1 focus-within:ring-ring',
                  disabled ? 'opacity-70' : 'cursor-grab active:cursor-grabbing hover:border-primary/50',
                  isDragged && 'border-primary bg-primary/5 opacity-50',
                  page.originalIndex !== index && 'border-primary/40',
                )}
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-muted-foreground">
                    {format(t.reorderPdfPages.currentPosition, { page: currentPage })}
                  </span>
                  <span
                    aria-label={t.reorderPdfPages.dragHandle}
                    className="text-muted-foreground/70"
                  >
                    ⋮⋮
                  </span>
                </div>

                <div className="flex aspect-[3/4] items-center justify-center overflow-hidden rounded border bg-background">
                  {thumbnail ? (
                    <img
                      src={thumbnail.dataUrl}
                      alt={format(t.pdfPreview.pageAlt, { page: originalPage })}
                      className="max-h-full max-w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-center">
                      <div className="text-2xl font-bold text-foreground">{originalPage}</div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {format(t.reorderPdfPages.originalPage, { page: originalPage })}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onMove(index, index - 1)}
                    disabled={disabled || index === 0}
                    aria-label={format(t.reorderPdfPages.moveUp, { page: originalPage })}
                    className="px-2"
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => onMove(index, index + 1)}
                    disabled={disabled || index === pages.length - 1}
                    aria-label={format(t.reorderPdfPages.moveDown, { page: originalPage })}
                    className="px-2"
                  >
                    ↓
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface ProgressPanelProps {
  progress: ReorderProgress
}

function ProgressPanel({ progress }: ProgressPanelProps) {
  const t = useT()
  const percent = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between gap-3 text-sm">
          <span className="font-medium text-foreground">{t.reorderPdfPages.progressTitle}</span>
          <span className="text-muted-foreground">
            {format(t.reorderPdfPages.progressPages, {
              current: progress.current,
              total: progress.total,
            })}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-background">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${percent}%` }}
          />
        </div>
      </CardContent>
    </Card>
  )
}

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  totalPages: number
  movedPages: number
  onReset: () => void
}

function ResultPanel({ file, blob, objectUrl, totalPages, movedPages, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = reorderedPdfFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.reorderPdfPages.totalPages}</span>
            <span className="font-medium text-foreground">{totalPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.reorderPdfPages.movedPages}</span>
            <span className="font-medium text-foreground">{movedPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.reorderPdfPages.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export function PomenyatPoryadokStranicPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  const isBusy = phase.kind === 'reading' || phase.kind === 'loading'
  const currentFile =
    phase.kind === 'reading' ||
    phase.kind === 'ready' ||
    phase.kind === 'loading' ||
    phase.kind === 'error'
      ? phase.file
      : null
  const currentTotalPages =
    phase.kind === 'ready' || phase.kind === 'loading'
      ? phase.totalPages
      : phase.kind === 'error'
        ? phase.totalPages
        : null
  const currentPages =
    phase.kind === 'ready' || phase.kind === 'loading' ? phase.pages : []
  const previewFile =
    currentFile && currentTotalPages !== null && currentTotalPages <= LARGE_PDF_WARNING_PAGES
      ? currentFile
      : null
  const pagePreview = usePdfPageThumbnails({
    file: previewFile,
    maxPages: 24,
  })
  const thumbnailByPage = useMemo(
    () => new Map(pagePreview.thumbnails.map((thumbnail) => [thumbnail.pageNumber, thumbnail])),
    [pagePreview.thumbnails],
  )

  const canSubmit = phase.kind === 'ready' && phase.totalPages > 1
  const movedPages = useMemo(() => countMovedPages(currentPages), [currentPages])

  async function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large')
      setPhase({
        kind: 'error',
        file,
        totalPages: null,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    setPhase({ kind: 'reading', file })

    try {
      const totalPages = await readPdfPageCount(file)
      setPhase({ kind: 'ready', file, totalPages, pages: buildInitialPageOrder(totalPages) })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      const message = code === 'encrypted' ? t.reorderPdfPages.encrypted : t.reorderPdfPages.parseError
      toolError(UTILITY, code)
      setPhase({ kind: 'error', file, totalPages: null, errorCode: code, message })
    }
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file && phase.totalPages !== null) {
      setPhase({
        kind: 'ready',
        file: phase.file,
        totalPages: phase.totalPages,
        pages: buildInitialPageOrder(phase.totalPages),
      })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  function handleMove(fromIndex: number, toIndex: number) {
    if (phase.kind !== 'ready') return
    if (toIndex < 0 || toIndex >= phase.pages.length || fromIndex === toIndex) return

    const next = [...phase.pages]
    const [page] = next.splice(fromIndex, 1)
    if (!page) return

    next.splice(toIndex, 0, page)
    setPhase({ ...phase, pages: next })
  }

  function handleResetOrder() {
    if (phase.kind !== 'ready') return
    setPhase({ ...phase, pages: buildInitialPageOrder(phase.totalPages) })
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    if (phase.totalPages < 2) {
      toolError(UTILITY, 'single_page', { total_pages: phase.totalPages })
      setPhase({
        kind: 'error',
        file: phase.file,
        totalPages: phase.totalPages,
        errorCode: 'single_page',
        message: t.reorderPdfPages.singlePage,
      })
      return
    }

    const file = phase.file
    const pages = phase.pages
    setPhase({
      kind: 'loading',
      file,
      totalPages: phase.totalPages,
      pages,
      progress: { current: 0, total: phase.totalPages },
    })
    toolInvoke(UTILITY, {
      total_pages: phase.totalPages,
      moved_pages: movedPages,
    })

    const startMs = Date.now()

    try {
      const { blob, totalPages, movedPages: resultMovedPages } = await reorderPdfPages(
        file,
        pages,
        (progress) => {
          setPhase({ kind: 'loading', file, totalPages: phase.totalPages, pages, progress })
        },
      )
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        total_pages: totalPages,
        moved_pages: resultMovedPages,
        output_bytes: blob.size,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        totalPages,
        movedPages: resultMovedPages,
      })
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message.toLowerCase() : ''
      const code = rawMessage.includes('encrypted')
        ? 'encrypted'
        : rawMessage.includes('page_order_mismatch')
          ? 'page_order_mismatch'
          : 'parse_error'
      const message = code === 'encrypted' ? t.reorderPdfPages.encrypted : t.reorderPdfPages.parseError
      toolError(UTILITY, code, { total_pages: phase.totalPages })
      setPhase({ kind: 'error', file, totalPages: phase.totalPages, errorCode: code, message })
    }
  }

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          totalPages={phase.totalPages}
          movedPages={phase.movedPages}
          onReset={handleClear}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge
          file={currentFile}
          totalPages={currentTotalPages}
          onClear={handleClear}
          disabled={isBusy}
        />
      ) : (
        <DropZone onFile={handleFile} disabled={isBusy} />
      )}

      {phase.kind === 'reading' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner />
          {t.reorderPdfPages.reading}
        </p>
      )}

      {phase.kind === 'ready' && phase.totalPages > LARGE_PDF_WARNING_PAGES && (
        <Card className="border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-500/10">
          <CardContent className="p-4 text-sm text-foreground">
            {format(t.reorderPdfPages.largePdf, { pages: phase.totalPages })}
          </CardContent>
        </Card>
      )}

      {phase.kind === 'ready' && phase.totalPages > 24 && phase.totalPages <= LARGE_PDF_WARNING_PAGES && (
        <p className="text-xs text-muted-foreground">
          {format(t.pdfPreview.limited, { limit: 24 })}
        </p>
      )}

      {phase.kind === 'ready' && phase.totalPages <= 1 && (
        <p className="text-sm text-muted-foreground">{t.reorderPdfPages.singlePage}</p>
      )}

      {(phase.kind === 'ready' || phase.kind === 'loading') && currentPages.length > 0 && (
        <PageGrid
          pages={currentPages}
          thumbnailByPage={thumbnailByPage}
          disabled={isBusy}
          onMove={handleMove}
          onReset={handleResetOrder}
        />
      )}

      {phase.kind === 'loading' && <ProgressPanel progress={phase.progress} />}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="sticky bottom-3 z-10 w-full shadow-lg"
        disabled={!canSubmit || isBusy}
        onClick={handleSubmit}
      >
        {phase.kind === 'loading' ? (
          <>
            <Spinner />
            {t.reorderPdfPages.loading}
          </>
        ) : (
          t.reorderPdfPages.submit
        )}
      </Button>
    </div>
  )
}
